import type { UsageHistory } from '../shared/Interfaces';

export interface ResumoMetadata {
    periodo: string;
    modelo: string;
    chave_api: string;
    usuario: string;
    exportado_em?: string;
}

export interface Resumo {
    saldo_disponivel: number;
    gastos_periodo: number;
    total_requisicoes: number;
    total_tokens_input: number;
    total_tokens_output: number;
    total_tokens_em_cache: number;
    custo_horario_pico: number;
    custo_noturno: number;
    custo_batch_api: number;
    custo_flex: number;
    custo_tokens_em_cache: number;
    custo_extracao_arquivos: number;
}

export interface RequestRow {
    data: any;
    horario_pico: number;
    fora_horario_pico: number;
    batch_api: number;
    flex: number;
    total: number;
}

interface ChartDataLike {
    labels?: any[];
    datasets?: Array<{ data?: any[] }>;
}

export function buildResumo(
    usageHistory: UsageHistory | undefined,
    credits: number,
): Resumo {
    return {
        saldo_disponivel: credits,
        gastos_periodo: usageHistory?.total_cost ?? 0,
        total_requisicoes: usageHistory?.total_requests ?? 0,
        total_tokens_input: usageHistory?.total_input_tokens ?? 0,
        total_tokens_output: usageHistory?.total_output_tokens ?? 0,
        total_tokens_em_cache: usageHistory?.total_cached_tokens ?? 0,
        custo_horario_pico: usageHistory?.total_peak_cost ?? 0,
        custo_noturno: usageHistory?.total_offpeak_cost ?? 0,
        custo_batch_api: usageHistory?.total_batch_api_cost ?? 0,
        custo_flex: usageHistory?.total_flex_cost ?? 0,
        custo_tokens_em_cache: usageHistory?.total_cached_cost ?? 0,
        custo_extracao_arquivos: usageHistory?.total_extraction_cost ?? 0,
    };
}

export function buildRequestsRows(chart: ChartDataLike | null | undefined): RequestRow[] {
    if (!chart || !chart.labels || !chart.datasets) return [];
    return chart.labels.map((label, i) => {
        const peak = Number(chart.datasets?.[0]?.data?.[i] ?? 0);
        const offpeak = Number(chart.datasets?.[1]?.data?.[i] ?? 0);
        const batch = Number(chart.datasets?.[2]?.data?.[i] ?? 0);
        const flex = Number(chart.datasets?.[3]?.data?.[i] ?? 0);
        return {
            data: label,
            horario_pico: peak,
            fora_horario_pico: offpeak,
            batch_api: batch,
            flex,
            total: peak + offpeak + batch + flex,
        };
    });
}

interface AllDataLike {
    metadata: { periodo: string; modelo: string; chave_api: string; usuario: string };
    resumo: Resumo;
}

export function buildResumoSheetRows(allData: AllDataLike): Array<{ Campo: string; Valor: number | string }> {
    return [
        { Campo: 'Período', Valor: allData.metadata.periodo },
        { Campo: 'Modelo', Valor: allData.metadata.modelo },
        { Campo: 'Chave API', Valor: allData.metadata.chave_api },
        { Campo: 'Usuário', Valor: allData.metadata.usuario },
        { Campo: 'Saldo Disponível (R$)', Valor: allData.resumo.saldo_disponivel },
        { Campo: 'Gastos do Período (R$)', Valor: allData.resumo.gastos_periodo },
        { Campo: 'Total de Requisições', Valor: allData.resumo.total_requisicoes },
        { Campo: 'Total Tokens Input', Valor: allData.resumo.total_tokens_input },
        { Campo: 'Total Tokens Output', Valor: allData.resumo.total_tokens_output },
        { Campo: 'Total Tokens em Cache', Valor: allData.resumo.total_tokens_em_cache },
        { Campo: 'Custo Horário de Pico (R$)', Valor: allData.resumo.custo_horario_pico },
        { Campo: 'Custo Noturno (R$)', Valor: allData.resumo.custo_noturno },
        { Campo: 'Custo Batch API (R$)', Valor: allData.resumo.custo_batch_api },
        { Campo: 'Custo Flex (R$)', Valor: allData.resumo.custo_flex },
        { Campo: 'Custo Extração de Arquivos (R$)', Valor: allData.resumo.custo_extracao_arquivos },
        { Campo: 'Custo Tokens em Cache (R$)', Valor: allData.resumo.custo_tokens_em_cache },
    ];
}
